import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  Button,
  Datagrid,
  DateInput,
  DeleteButton,
  DeleteWithConfirmButton,
  Edit,
  FormDataConsumer,
  FormTab,
  FunctionField,
  ImageField,
  ImageInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  minValue,
  required,
  useDataProvider,
  useEditContext,
  useGetList,
  useGetOne,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useSaveContext,
  useTranslate
} from 'react-admin';

import { AddCircle as AddCircleIcon, Cancel as CancelIcon, ListAlt as ReadjustIcon, RemoveCircle as RemoveCircleIcon, Save as SaveIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, Modal, Radio, RadioGroup, Switch, TextField as TextFieldMui, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useFormContext, useFormState } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { CheckChainUsesModule, CouponIntegrationTestButton, DiscountPercentageInput, ImageInformations, LazyLoadAutoCompleteInput, ModuleKey, PriceInput, Tooltip } from '../../components';
import AddCustomItemButton from '../../components/AddCustomItemButton';
import CustomSelect from '../../components/CustomSelect';
import Permission from '../../components/Permission';
import { MAX_DATE, PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { IntegrationType, ProductCostValueType, TagType } from '../../models';
import { SislavProductFields, sislavPlaceFields } from '../../components/SislavFields';
import { dateInputFormatter, filterToQuery, parseCurrency } from '../../utils';
import ProductStatement from './ProductStatement';
import { StockPlacesFields } from './StockPlacesFields';

// Style constants for sx props
const sxStyles = {
  arrayInput: {
    '& p': {
      color: 'grey',
      marginRight: '1em',
      fontWeight: 'bold',
    },
  },
  placeName: {
    padding: '1em 0 0.5em 0',
    width: '100%',
  },
  stockSwitch: {
    margin: '2em 2em 0em 1em'
  },
  Toolbar: {
    justifyContent: 'space-between'
  },
  ToolbarButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '250px'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 1,
    paddingTop: 1,
  },
  table: {
    border: '1px solid #ccc',
  },
  boxModal: {
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: 1,
    boxShadow: 24,
    left: '50%',
    maxHeight: '75%',
    padding: '20px 24px',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
  },
};

const GiftAlertCard = (props) => (
  <Alert variant="outlined" severity="info" {...props}>
    <AlertTitle>Clube de Vantagens</AlertTitle>
    Há uma recompensa vinculado a esse produto. Certifique que suas alterações sejam feitas no cadastro do Clube de Vantagens também.
  </Alert>
);

const CustomToolbar = ({ hasReadjustment, setShowModal, setHasGift, ...props }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { resource } = useEditContext();
  const record = useRecordContext();
  const { save } = useSaveContext();
  const { trigger } = useFormContext();
  const { dirtyFields, isSubmitting } = useFormState();
  const isDirty = Object.keys(dirtyFields).length > 0;

  const { data: chain } = useGetOne('chains', { id: getChainId() });
  const { data: gifts } = useGetList('gifts', { filter: { productId: record.id } })

  if (!record) { return null; }

  const handleSaveButton = async (formData) => {

    const isValid = await trigger();

    if (!isValid) { return; }

    let hasGift = false;
    if (chain && chain.useGift && gifts) {
      hasGift = gifts.length > 0;
    }

    setHasGift(hasGift);

    if (hasGift || hasReadjustment) {
      setShowModal(true);
    } else {
      save(formData);
    }
  }

  return (
    <Toolbar sx={sxStyles.Toolbar}>
      <FormDataConsumer>
        {({ formData }) =>
          <Button
            startIcon={isSubmitting ? <CircularProgress size={20} /> : hasReadjustment ? <ReadjustIcon /> : <SaveIcon />}
            disabled={isSubmitting || (!isDirty && !hasReadjustment)}
            label={hasReadjustment ? 'Verificar reajustes' : 'Salvar'}
            size="medium"
            variant="contained"
            onClick={() => handleSaveButton(formData)}
          />
        }
      </FormDataConsumer>
      <Permission permission={PERMISSIONS.DISABLE_PRODUCT}>
        <Box sx={sxStyles.ToolbarButtonWrapper}>
          <Button
            startIcon={record.isDisabled ? <AddCircleIcon /> : <RemoveCircleIcon />}
            label={record.isDisabled ? 'Ativar' : 'Desativar'}
            onClick={() => {
              dataProvider.update(resource, { id: record.id, data: { isDisabled: !record.isDisabled }, previousData: { ...record } })
                .then(() => {
                  notify(`Produto ${record.isDisabled ? 'ativado' : 'desativado'} com sucesso`)
                }).catch(() => {
                  notify(`Erro ao ${record.isDisabled ? 'ativar' : 'desativar'} produto`, { type: 'warning' })
                });

              refresh();
            }}
          />

          {
            gifts?.length > 0 ?
              <DeleteWithConfirmButton
                confirmTitle="Aviso"
                confirmContent={
                  <GiftAlertCard />
                }
              />
              :
              <DeleteButton />
          }
        </Box>
      </Permission>
    </Toolbar>
  );
}

const getCurrentValue = (prev, payload) => {
  switch (payload.action) {
    case 'increase':
      return +prev[payload.type] + (+payload.value || 0);
    case 'decrease':
      return +prev[payload.type] - (+payload.value || 0);
    case 'fixed':
      return payload.value;
    default:
      break;
  }
}

const AlertModal = ({ open, onClose, data, ids, state: { type, action, value }, formData, hasReadjustment, hasGift }) => {
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const record = useRecordContext();
  const { save } = useSaveContext();

  const handleSave = () => {

    if (!hasReadjustment) {
      onClose();
      save(formData);
      return;
    }

    setLoading(true);
    const newProductPlaces = formData.places.map((item) => {
      if (ids.includes(item.placeId)) {
        return {
          ...item,
          [type]: getCurrentValue(item, { type, action, value })
        };
      }

      return item;
    });
    const newData = { ...formData, places: newProductPlaces };

    const hasNegativeValues = newProductPlaces.some((item) => item[type] < 0);

    if (hasNegativeValues) {
      notify('Os valores atualizados devem ser maior que 0', { type: 'warning' });
      onClose();
    } else {
      formData.places = newProductPlaces;
      dataProvider.update('chains/chainId/products', {
        id: record.id, data: newData, previousData: { ...record }
      })
        .then(() => {
          onClose();
          notify('Reajuste realizado com sucesso');
        }).catch(() => {
          onClose();
          notify('Erro ao realizar reajuste', { type: 'warning' });
        })
        .finally(() => {
          redirect('/chains/chainId/products');
          setLoading(false);
        });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={sxStyles.boxModal}>
        <Typography variant="h6">
          Avisos
        </Typography>
        {
          hasReadjustment &&
          <>
            <Typography variant="subtitle1">
              Estes serão os valores atualizados. Deseja continuar?
            </Typography>
            <div style={{ overflowY: 'auto', maxHeight: '65vh', marginTop: 8 }}>
              <Datagrid
                sort={{ field: 'name', order: 'ASC' }}
                bulkActionButtons={false}
                data={data}
                sx={{ '& .RaDatagrid-table': { border: '1px solid #ccc' } }}
              >
                <TextField label="Ponto de Troca" source="name" />
                <FunctionField
                  label="Valor Anterior"
                  render={(record) => type === 'partnerPrice' ? `R$ ${record[type]}` : record[type]}
                />
                <FunctionField
                  label="Valor Atualizado"
                  render={(record) => type === 'partnerPrice' ? `R$ ${getCurrentValue(record, { type, action, value })}` : getCurrentValue(record, { type, action, value })}
                />
              </Datagrid>
            </div>
          </>
        }

        {
          hasGift &&
          <GiftAlertCard sx={{ marginTop: 2, marginBottom: 2 }} />
        }

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', padding: 0 }}>
          <Button
            startIcon={<CancelIcon />}
            variant="outlined"
            onClick={onClose}
            label="Cancelar"
            size="medium"
            disabled={loading}
          />
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={handleSave}
            label="Salvar"
            size="medium"
            disabled={loading}
          />
        </div>
      </Box>
    </Modal>
  );
}

const ReadjustmentTab = ({ showModal, setShowModal, hasReadjustment, hasGift, setHasReadjustment, data, placeOpts }) => {
  const [selectsAllPlaces, setselectsAllPlaces] = useState(false);
  const [productPlaces, setProductPlaces] = useState([]);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState([]);
  const [action, setAction] = useState();
  const [type, setType] = useState();
  const [value, setvalue] = useState();

  const formatCurrency = (v) => {
    const parsed = parseCurrency(v, 2);
    return parsed || '';
  }

  const validation = (v, max) => (typeof v === 'number' && v < max);

  useEffect(() => {
    const currProductPlaces = data.places?.map(p => {
      const placeName = placeOpts.find(({ id }) => id === p?.placeId);
      return placeName && { ...p, ...placeName };
    }).filter(item => item);

    setProductPlaces(currProductPlaces || []);
  }, [data.places, placeOpts]);

  useEffect(() => {
    if (type && action && value && selectedPlaceIds?.length) {
      setHasReadjustment(true);
    } else {
      setHasReadjustment(false);
    }
  }, [type, action, value, selectedPlaceIds]);

  useEffect(() => {
    if (selectsAllPlaces) {
      setSelectedPlaceIds(productPlaces.map(({ id }) => id));
    } else {
      setSelectedPlaceIds([]);
    }
  }, [selectsAllPlaces]);

  const dataResult = [];

  productPlaces.forEach(({ id, name, price, partnerPrice }) => {
    dataResult.push({ id, name, price, partnerPrice });
  });

  return (
    productPlaces.length ? (
      <Box sx={{ maxWidth: 800, width: '100%' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            {!selectsAllPlaces &&
              <CustomSelect
                label="Pontos de Troca"
                choices={productPlaces}
                value={selectedPlaceIds}
                onChange={setSelectedPlaceIds}
                multiple
              />}
            <FormControlLabel
              label="Selecionar todos os locais"
              style={{ display: 'block', marginBottom: 16 }}
              control={<Switch onChange={({ target }) => setselectsAllPlaces(target.checked)} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl>
              <FormLabel>Tipo de valor</FormLabel>
              <RadioGroup row onChange={({ target }) => setType(target.value)}>
                <FormControlLabel value="price" control={<Radio />} label="Pontos" />
                <FormControlLabel value="partnerPrice" control={<Radio />} label="Reais" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <CustomSelect
              label="Tipo de ação"
              value={action}
              onChange={setAction}
              choices={[{ id: "increase", name: "Aumentar" }, { id: "decrease", name: "Diminuir" }, { id: "fixed", name: "Fixo" }]}
              variant="filled"
              small
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {type === 'price' &&
              <TextFieldMui
                label="Valor em pontos"
                value={value}
                onChange={({ target }) => setvalue(target.value.replace(/[^\d]/g, ''))}
                error={value && value < 1}
                helperText={value && value < 1 && "Deve ser no mínimo 1"}
                variant="filled"
                size="small"
                fullWidth
              />}
            {type === 'partnerPrice' &&
              <TextFieldMui
                label="Valor em reais"
                value={value && `R$ ${(+value).toFixed(2)}`}
                onChange={({ target }) => setvalue(formatCurrency(target.value))}
                error={validation(value, 0.01)}
                helperText={validation(value, 0.01) && "Deve ser no mínimo R$ 0.01"}
                variant="filled"
                size="small"
                fullWidth
              />}
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" style={{ marginTop: 16 }}>
              * Todos os campos precisam ser preenchidos para realizar as alterações.
            </Typography>
          </Grid>
        </Grid>
        <FormDataConsumer>
          {({ formData }) =>
            <AlertModal
              data={dataResult}
              ids={selectedPlaceIds}
              open={showModal}
              onClose={() => setShowModal(false)}
              state={{ type, action, value }}
              formData={formData}
              hasReadjustment={hasReadjustment}
              hasGift={hasGift}
            />
          }
        </FormDataConsumer>
      </Box>
    ) : <Typography variant="subtitle2">Nenhum local cadastrado para este produto.</Typography>
  );
}

// Componente memoizado para os campos de combustível de brinde
const GiftFuelFieldsInner = ({ scopedFormData, fuels, parentScopedFormData }) => {
  const selectedfuelIds = useMemo(() => {
    return parentScopedFormData?.productPlaceGiftFuels
      ? parentScopedFormData.productPlaceGiftFuels.filter((p) => p).map(p => p.fuelId)
      : [];
  }, [parentScopedFormData?.productPlaceGiftFuels]);

  const availableFuels = useMemo(() => {
    return fuels.filter(
      ({ id }) => !selectedfuelIds.includes(id) ||
        (scopedFormData ? scopedFormData.fuelId === id : false),
    );
  }, [fuels, selectedfuelIds, scopedFormData?.fuelId]);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1, alignItems: 'center' }}>
      <NumberInput
        label={"Quantidade mínima (litros)"}
        source="minimumAmount"
        validate={[required(), minValue(0)]}
        sx={{ width: 180 }}
      />
      <SelectInput
        label="Combustível"
        source="fuelId"
        choices={availableFuels}
        optionText="name"
        validate={[required()]}
        sx={{ width: 160 }}
      />
      <DateInput label="Data inicial" source="startDate" format={dateInputFormatter} inputProps={{ max: MAX_DATE }} sx={{ width: 150 }} />
      <DateInput label="Data final" source="endDate" format={dateInputFormatter} inputProps={{ max: MAX_DATE }} validate={minValue(scopedFormData?.startDate, `Deve ser no mínimo ${dayjs(scopedFormData?.startDate).format("DD/MM/YYYY")}`)} sx={{ width: 150 }} />
      <NumberInput
        label="Limite de resgate"
        source="limitQuantity"
        parse={v => {
          if (v < 0) { return 0; }
          return !v || v === '' ? null : Math.floor(v);
        }}
        sx={{ width: 130 }}
      />
      <BooleanInput label="Validar em outro local" source="redeemAnywhere" />
    </Box>
  );
};
const GiftFuelFields = memo(GiftFuelFieldsInner);

// Componente memoizado para os campos de integração (RTI, EVO, Sanvitron)
const IntegrationFieldsInner = ({ scopedFormData, placeOpts, translate }) => {
  const record = useRecordContext();
  const place = useMemo(() => {
    return placeOpts.find((place) => place.id === scopedFormData?.placeId);
  }, [placeOpts, scopedFormData?.placeId]);

  if (!scopedFormData || !placeOpts.length) return null;

  // Botão "Testar" ao lado do campo: dispara a chamada real ao parceiro com o
  // token atual do posto e o código digitado aqui, sem gerar cupom no PontuaX.
  // Só admin vê (o próprio componente se esconde).
  const testButton = (externalCode) => (
    <CouponIntegrationTestButton
      compact
      placeId={scopedFormData.placeId}
      productId={record?.id}
      integration={place?.integration}
      externalCode={externalCode}
      disabled={!scopedFormData.placeId}
      disabledReason="Escolha o ponto de troca primeiro."
    />
  );

  if (place?.integration === IntegrationType.rti) {
    return (
      <>
        <NumberInput
          source="rtiCode"
          label="Código RTI"
          style={{ width: 140 }}
          validate={[minValue(0)]}
          min={0}
        />
        {testButton(scopedFormData.rtiCode)}
      </>
    );
  }

  if (place?.integration === IntegrationType.evo) {
    return (
      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} title={translate("tooltips.isEvoActive")} placement="top-start">
        <BooleanInput
          label="Integrar com EVO"
          source="isEvoActive"
          sx={{ alignSelf: "center", marginLeft: 2 }}
          disabled={scopedFormData.isSanvitron}
        />
      </Tooltip>
    );
  }

  if (place?.integration === IntegrationType.trevo) {
    return (
      <>
        <NumberInput
          source="trevoServiceId"
          label="Código Serviço Trevo"
          style={{ width: 180 }}
          validate={[minValue(0)]}
          min={0}
        />
        {testButton(scopedFormData.trevoServiceId)}
      </>
    );
  }

  if (place?.integration === IntegrationType.sislav) {
    return <SislavProductFields />;
  }

  // Limpar valores se não for RTI/EVO/Trevo
  if (scopedFormData.rtiCode !== null) scopedFormData.rtiCode = null;
  if (scopedFormData.isEvoActive !== false) scopedFormData.isEvoActive = false;
  if (scopedFormData.trevoServiceId !== null && scopedFormData.trevoServiceId !== undefined) scopedFormData.trevoServiceId = null;

  return null;
};
const IntegrationFields = memo(IntegrationFieldsInner);

// Componente memoizado para Sanvitron
const SanvitronFieldInner = ({ scopedFormData, placeOpts, translate }) => {
  const place = useMemo(() => {
    return placeOpts.find((place) => place.id === scopedFormData?.placeId);
  }, [placeOpts, scopedFormData?.placeId]);

  if (!place?.useSanvitronIntegration) return null;

  return (
    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} title={translate("tooltips.productPlaceIsSanvitron")} placement="top-start">
      <BooleanInput
        label="Integrar com Sanvitron"
        source="isSanvitron"
        sx={{ alignSelf: "center", marginLeft: 2 }}
        disabled={scopedFormData?.isEvoActive}
      />
    </Tooltip>
  );
};
const SanvitronField = memo(SanvitronFieldInner);

// Componente memoizado para Teixeira Pinto
const TeixeiraPintoFieldInner = ({ scopedFormData, placeOpts }) => {
  const place = useMemo(() => {
    return placeOpts.find((place) => place.id === scopedFormData?.placeId);
  }, [placeOpts, scopedFormData?.placeId]);

  if (place?.integration !== IntegrationType.teixeiraPinto) return null;

  return (
    <BooleanInput
      label="Integrar com Teixeira Pinto"
      source="isTeixeiraPinto"
      sx={{ alignSelf: "center", marginLeft: 2 }}
    />
  );
};
const TeixeiraPintoField = memo(TeixeiraPintoFieldInner);

// Componente memoizado para cada item do places array
const PlaceItemFieldsInner = ({ formData, scopedFormData, placeOpts, translate }) => {
  const selectedPlacesIds = useMemo(() => {
    return formData.places ? formData.places.filter((p) => p).map(p => p.placeId) : [];
  }, [formData.places]);

  const availablePlaces = useMemo(() => {
    return placeOpts.filter(
      ({ id }) => !selectedPlacesIds.includes(id) ||
        (scopedFormData ? scopedFormData.placeId === id : false),
    );
  }, [placeOpts, selectedPlacesIds, scopedFormData?.placeId]);

  // Nome completo do posto selecionado — vira tooltip, porque mesmo com o campo
  // maior um nome muito longo ainda pode ser cortado com reticências.
  const selectedPlaceName = useMemo(() => {
    return placeOpts.find(({ id }) => id === scopedFormData?.placeId)?.name || '';
  }, [placeOpts, scopedFormData?.placeId]);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
      {formData.places && (
        <AutocompleteInput
          source="placeId"
          choices={availablePlaces}
          label="Ponto de Troca"
          optionText="name"
          // Cresce com o espaço livre da linha em vez de largura fixa: com 200px
          // "Posto Dom Pedro ..." saía truncado como "Posto Dom Pe...".
          // A base cobre os nomes reais mais longos da base ("Posto Dom Pedro
          // Contagem Fernão Dias Rodovia"); se não couber, os campos seguintes
          // quebram pra próxima linha em vez de espremer o nome.
          sx={{ flex: '1 1 480px', minWidth: 280, maxWidth: 620 }}
          // O popup do MUI copia a largura do input; sem isso as opções da lista
          // ficam cortadas do mesmo jeito.
          slotProps={{ paper: { sx: { width: 'max-content', minWidth: '100%', maxWidth: 520 } } }}
          TextFieldProps={{ title: selectedPlaceName }}
          validate={[required()]}
        />
      )}
      <CheckChainUsesModule module={ModuleKey.DOTZ}>
        <SelectInput
          source="costValueType"
          label="Forma de troca"
          choices={ProductCostValueType.productCostValueTypeChoices}
          defaultValue={ProductCostValueType.points}
          sx={{ width: 150 }}
        />
      </CheckChainUsesModule>
      <TextInput
        source="costValueType"
        defaultValue={ProductCostValueType.points}
        sx={{ display: 'none' }}
      />
      {scopedFormData && scopedFormData.costValueType === ProductCostValueType.dotz ? (
        <PriceInput
          source="costValue"
          label="Valor Dotz"
          validate={[required(), minValue(0)]}
          sx={{ width: 120 }}
        />
      ) : (
        <NumberInput
          source="price"
          label="Valor em pontos"
          validate={[required(), minValue(0)]}
          min={0}
          sx={{ width: 130 }}
        />
      )}
      <PriceInput source="partnerPrice" label="Valor em reais (Parceiro)" sx={{ width: 180 }} />
      <NumberInput
        source="limit"
        label="Resgates por dia"
        min={1}
        validate={minValue(1)}
        sx={{ width: 130 }}
      />
      <DiscountPercentageInput />
      <IntegrationFields
        scopedFormData={scopedFormData}
        placeOpts={placeOpts}
        translate={translate}
      />
      <SanvitronField
        scopedFormData={scopedFormData}
        placeOpts={placeOpts}
        translate={translate}
      />
      <TeixeiraPintoField
        scopedFormData={scopedFormData}
        placeOpts={placeOpts}
      />
    </Box>
  );
};
const PlaceItemFields = memo(PlaceItemFieldsInner);

const edit = () => {
  const [hasReadjustment, setHasReadjustment] = useState(false);
  const [hasGift, setHasGift] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fuels, setFuels] = useState([]);
  const [placeOpts, setPlaceOpts] = useState([]);
  const translate = useTranslate();
  const { data: chain } = useGetOne('chains', { id: getChainId() });

  const dataProvider = useDataProvider();

  useEffect(() => {
    dataProvider.getList(`chains/${getChainId()}/places`, { filter: {}, sort: { field: 'name', order: 'ASC' } })
      .then(({ data }) => {
        setPlaceOpts(data);
      });

    dataProvider.getList('fuels', { filter: {}, sort: { field: 'name', order: 'ASC' } })
      .then(({ data }) => {
        setFuels(data);
      });
  }, [])

  const validate = [required()];

  const saleProductOptionRenderer = choice => `${choice.code} - ${choice.name}`;

  const transform = data => {

    data.places = data.places.map(place => {
      const placeOpt = placeOpts.find((opt) => place.placeId === opt.id);
      return {
        ...place,
        isGift: place.isGift ?? false,
        isEvoActive: placeOpt?.integration === IntegrationType.evo ? place.isEvoActive : false,
        rtiCode: placeOpt?.integration === IntegrationType.rti ? place.rtiCode : null,
        trevoServiceId: placeOpt?.integration === IntegrationType.trevo ? (place.trevoServiceId ?? null) : null,
        isSanvitron: placeOpt?.useSanvitronIntegration ? place.isSanvitron : false,
        isTeixeiraPinto: placeOpt?.integration === IntegrationType.teixeiraPinto ? place.isTeixeiraPinto : false,
        ...sislavPlaceFields(placeOpt?.integration === IntegrationType.sislav, place),
        productPlaceGiftFuels: (place.productPlaceGiftFuels || []).map(ppgf => ({
          ...ppgf,
          startDate: ppgf.startDate ? dayjs(ppgf.startDate).startOf('day') : null,
          endDate: ppgf.endDate ? dayjs(ppgf.endDate).endOf('day') : null,
        })),
      };
    });

    if (data.productBundles && data.productBundles.length > 0) {
      data.productBundles = data.productBundles.map(bundle => {
        const { formSaleProductIds, ...bundleData } = bundle;

        return (
          {
            ...bundleData,
            saleProductIds: Array.isArray(formSaleProductIds)
              ? formSaleProductIds.map(item => item.saleProductId)
              : []
          }
        )
      })
    } else {
      delete data.productBundles;
    }

    if (data.imagePath?.rawFile) {
      data.imagefile = data.imagePath;
    }
    return data;
  };

  return (
    <Permission permission={PERMISSIONS.UPDATE_PRODUCT}>
      <Edit title="Editar produto"
        transform={transform}
        queryOptions={{
          select: (data) => {
            if (typeof data.imagePath === 'string') {
              data.imagePath = {
                src: data.imagePath,
              }
            }
            if (data.productBundles) {
              data.productBundles = data.productBundles.map(bundle => {
                return (
                  {
                    ...bundle,
                    formSaleProductIds: Array.isArray(bundle.saleProductIds)
                      ? bundle.saleProductIds.map(id => ({ saleProductId: id }))
                      : []
                  }
                )
              })
            }
            return data;
          }
        }}
      >
        <TabbedForm
          sanitizeEmptyValues
          toolbar={<CustomToolbar
            setShowModal={setShowModal}
            hasReadjustment={hasReadjustment}
            setHasGift={setHasGift}
          />}
        >
          <FormTab label="Produto">
            <Box sx={{ maxWidth: 800, width: '100%' }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextInput source="name" label="Nome" validate={validate} fullWidth />
                </Grid>
                <Grid size={12}>
                  <TextInput source="description" label="Descrição" validate={validate} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <ReferenceInput
                    source="categoryId"
                    reference="chains/chainId/product-categories"
                    perPage={null}
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <SelectInput label="Categoria" optionText="name" emptyText="Nenhuma" fullWidth />
                  </ReferenceInput>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <ReferenceArrayInput
                    sort={{ field: 'name', order: 'ASC' }}
                    filter={{ type: TagType.product }}
                    perPage={null}
                    source="tagIds"
                    reference="chains/chainId/tag"
                  >
                    <SelectArrayInput label="Etiquetas de produto" optionText="name" fullWidth />
                  </ReferenceArrayInput>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Tooltip title={translate("tooltips.hiddenProductIds")} placement="top-start" sxContainer={{ alignItems: 'flex-start' }}>
                    <ReferenceArrayInput
                      source="hiddenProductIds"
                      reference="chains/chainId/products"
                      sort={{ field: "name", order: "ASC" }}
                    >
                      <AutocompleteArrayInput label="Produtos ocultos" optionText="name" fullWidth filterToQuery={filterToQuery} />
                    </ReferenceArrayInput>
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Tooltip title={translate("tooltips.productCustomerTags")} placement="top-start" sxContainer={{ alignItems: 'flex-start' }}>
                    <ReferenceArrayInput
                      source="customerTagIds"
                      sort={{ field: 'name', order: 'ASC' }}
                      reference="chains/chainId/tag"
                      filter={{ type: TagType.client }}
                      perPage={null}
                    >
                      <SelectArrayInput label="Etiqueta de clientes" optionText="name" fullWidth />
                    </ReferenceArrayInput>
                  </Tooltip>
                </Grid>
                <Grid size={12}>
                  <ImageInput source="imagePath" label="Imagem" accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}>
                    <ImageField source="src" label="Imagem" />
                  </ImageInput>
                  <ImageInformations />
                </Grid>
              </Grid>
            </Box>
          </FormTab>
          <FormTab label="Resgate">
            <FormDataConsumer>
              {({ formData: upperFormData }) => {
                const disableRemove = upperFormData.places?.length === 1;
                const disableAdd = upperFormData.places?.length === placeOpts.length;
                return (
                  <>
                    {/* Aviso do módulo, não da linha. Dentro do SimpleFormIterator
                        ele era repetido uma vez por posto. */}
                    {chain?.useGift && (
                      <Alert severity="info" variant="outlined" sx={{ mb: 2, maxWidth: 800 }}>
                        <AlertTitle>Clube de Vantagens</AlertTitle>
                        O cadastro de brindes agora é feito no <Link to="/gifts">Clube de Vantagens</Link>.
                      </Alert>
                    )}
                    <ArrayInput source="places" label="" sx={sxStyles.arrayInput}>
                      <SimpleFormIterator
                        disableReordering
                        disableRemove={disableRemove}
                        disableAdd={disableAdd}
                        fullWidth
                      >
                        <FormDataConsumer>
                          {({ formData, scopedFormData }) => (
                            <PlaceItemFields
                              formData={formData}
                              scopedFormData={scopedFormData}
                              placeOpts={placeOpts}
                              translate={translate}
                            />
                          )}
                        </FormDataConsumer>
                        {!chain?.useGift && (
                          <>
                            <FormDataConsumer>
                              {({ scopedFormData }) => (
                                scopedFormData?.costValueType !== ProductCostValueType.dotz && (
                                  <BooleanInput
                                    label="Brinde"
                                    source="isGift"
                                  />
                                )
                              )}
                            </FormDataConsumer>
                            <FormDataConsumer>
                              {({ scopedFormData }) => {
                                const disableAddFuel = scopedFormData?.productPlaceGiftFuels?.length === fuels.length;
                                return scopedFormData?.isGift && (
                                  <ArrayInput source="productPlaceGiftFuels" label="Combustíveis">
                                    <SimpleFormIterator
                                      disableAdd={disableAddFuel}
                                      disableReordering
                                      addButton={<AddCustomItemButton name="productPlaceGiftFuels" resetFields={{}} />}
                                    >
                                      <FormDataConsumer>
                                        {({ scopedFormData: placeProductScoped }) => (
                                          <GiftFuelFields
                                            scopedFormData={placeProductScoped}
                                            fuels={fuels}
                                            parentScopedFormData={scopedFormData}
                                          />
                                        )}
                                      </FormDataConsumer>
                                    </SimpleFormIterator>
                                  </ArrayInput>
                                );
                              }}
                            </FormDataConsumer>
                          </>
                        )}
                      </SimpleFormIterator>
                    </ArrayInput>
                  </>
                );
              }}
            </FormDataConsumer>
          </FormTab>
          <FormTab label="Reajuste">
            <FormDataConsumer>
              {({ formData }) =>
                <ReadjustmentTab
                  showModal={showModal}
                  setShowModal={setShowModal}
                  hasReadjustment={hasReadjustment}
                  hasGift={hasGift}
                  setHasReadjustment={setHasReadjustment}
                  data={formData}
                  placeOpts={placeOpts}
                />
              }
            </FormDataConsumer>
          </FormTab>
          <FormTab label="Estoque">
            <Alert severity="info" sx={{ mb: 1, mt: 2, maxWidth: 800 }}>
              A entrada e baixa de produtos no estoque devem ser realizadas nas respectivas telas:{' '}
              <Link to="/warehouse/product-entry">Entrada de produto</Link> e{' '}
              <Link to="/warehouse/product-write-off">Baixa de produto</Link>.
            </Alert>
            <BooleanInput
              label="Controlar estoque"
              source="stock"
              sx={sxStyles.stockSwitch}
            />
            {/* Sem <ArrayInput source="places"> aqui: a aba Resgate já tem um, e
                dois useFieldArray no mesmo source duplicavam linha ao remover
                (ver comentário em StockPlacesFields.jsx). */}
            <FormDataConsumer>
              {({ formData }) => formData.stock && (
                <StockPlacesFields
                  places={formData.places}
                  placeOpts={placeOpts}
                />
              )}
            </FormDataConsumer>
          </FormTab>
          <FormTab label="Integração">
            <ArrayInput source="productBundles" label="Ofertas de Produtos">
              <SimpleFormIterator disableReordering>
                <FormDataConsumer>
                  {({ scopedFormData: bundleScopedFormData }) => {
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                        <NumberInput
                          source="quantity"
                          label="Quantidade"
                          validate={[required(), minValue(1)]}
                          parse={(value) => (isNaN(value) || value <= 0 ? null : Math.round(value))}
                          sx={{ width: 120 }}
                        />
                        <ArrayInput
                          label="Produtos da Oferta"
                          source="formSaleProductIds"
                          sx={{ ...sxStyles.arrayInput, minWidth: 300 }}
                          validate={[required()]}
                        >
                          <SimpleFormIterator disableReordering>
                            <FormDataConsumer>
                              {({ scopedFormData }) => {
                                const selectedProductIds = bundleScopedFormData?.formSaleProductIds ? bundleScopedFormData.formSaleProductIds.filter((p) => p).map(
                                  p => p.saleProductId,
                                ) : [];

                                return (
                                  <LazyLoadAutoCompleteInput
                                    label="Produto de Venda"
                                    source="saleProductId"
                                    format={saleProductOptionRenderer}
                                    reference="chains/chainId/sale-products"
                                    sx={{ width: 350 }}
                                    validate={[required()]}
                                    choicesFilter={(choices) => {
                                      return choices.filter(choice =>
                                        !selectedProductIds.includes(choice.id) ||
                                        (scopedFormData && scopedFormData.saleProductId === choice.id)
                                      );
                                    }}
                                  />
                                )
                              }}
                            </FormDataConsumer>
                          </SimpleFormIterator>
                        </ArrayInput>
                      </Box>
                    )
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </FormTab>
          <Permission permission={PERMISSIONS.STOCK_CHANGES_REPORT}>
            <FormTab label="Extrato" path="transactions">
              <FormDataConsumer>
                {({ formData }) => {
                  return <ProductStatement id={formData.id} />
                }}
              </FormDataConsumer>
            </FormTab>
          </Permission>
        </TabbedForm>
      </Edit>
    </Permission >
  )
}

export default edit
